import React, { useMemo, useState } from 'react';
import { ExternalLink, MapPin } from 'lucide-react';
import { APIProvider } from '@vis.gl/react-google-maps';
import { Badge, Card, PageHeader, StatCard } from '../../../components/ui';
import { ErrorBoundary } from '../../../components/ErrorBoundary';
import { useRoutingData } from './useRoutingData';
import type { VehicleRoute } from './useRoutingData';
import { RouteMap } from './RouteMap';

/**
 * Route Optimizer
 * Renders pre-computed vehicle routes on a Google Map with real-road polylines,
 * per-vehicle side panels, and a fleet-justification banner.
 */

// Fixed, legible palette reused for both a route's card accent and its polyline.
const ROUTE_COLORS = [
	'#00a76f', // brand green
	'#ff5630', // error red
	'#00b8d9', // info cyan
	'#8e33ff', // secondary purple
	'#ffab00', // warning amber
	'#1c252e', // ink
	'#22c55e', // success green
	'#0284c7', // blue
	'#d97706', // dark amber
	'#b91c1c', // dark red
];

const GMAP_API_KEY = import.meta.env.VITE_GMAP_API_KEY as string | undefined;

const TRIP_TYPE_META: Record<
	number,
	{ label: string; type: 'dispatch' | 'pickup' | 'default'; icon: string }
> = {
	1: { label: 'Dispatch', type: 'dispatch', icon: '🚛' },
	2: { label: 'Pickup', type: 'pickup', icon: '🚚' },
	3: { label: 'Dispatch & Pickup', type: 'default', icon: '🔁' },
};

const TripTypeBadge: React.FC<{ tripType: number }> = ({ tripType }) => {
	const meta = TRIP_TYPE_META[tripType] ?? TRIP_TYPE_META[1];
	return (
		<Badge variant='type' type={meta.type} icon={meta.icon}>
			{meta.label}
		</Badge>
	);
};

interface VehicleCardProps {
	route: VehicleRoute;
	color: string;
	active: boolean;
	onSelect: () => void;
}

const VehicleCard: React.FC<VehicleCardProps> = ({ route, color, active, onSelect }) => {
	const clientStops = route.stops.filter(stop => stop.id !== 'DEPOT').length;

	return (
		<button
			type='button'
			onClick={onSelect}
			aria-pressed={active}
			className='block w-full text-left'
		>
			<Card
				className={`border-l-4 transition-shadow ${
					active ? 'shadow-z8 ring-2 ring-primary/30' : 'hover:shadow-z4'
				}`}
				style={{ borderLeftColor: color }}
			>
				<div className='flex items-start justify-between gap-2'>
					<div className='flex items-center gap-2'>
						<span
							className='inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold text-white'
							style={{ backgroundColor: color }}
						>
							{route.vehicle}
						</span>
						<div>
							<div className='text-sm font-semibold text-gray-900'>Vehicle {route.vehicle}</div>
							<div className='text-xs text-gray-500'>Slot {route.slot}</div>
						</div>
					</div>
					<TripTypeBadge tripType={route.trip_type} />
				</div>

				<div className='mt-3 grid grid-cols-3 gap-2 text-center'>
					<div>
						<div className='text-sm font-semibold text-gray-900'>{route.distance_km} km</div>
						<div className='text-[11px] text-gray-500'>Distance</div>
					</div>
					<div>
						<div className='text-sm font-semibold text-gray-900'>{route.duration_min} min</div>
						<div className='text-[11px] text-gray-500'>Duration</div>
					</div>
					<div>
						<div className='text-sm font-semibold text-gray-900'>{clientStops}</div>
						<div className='text-[11px] text-gray-500'>Stops</div>
					</div>
				</div>

				<a
					href={route.maps_link}
					target='_blank'
					rel='noopener noreferrer'
					onClick={e => e.stopPropagation()}
					className='mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-info-600 hover:underline'
				>
					<ExternalLink className='h-3.5 w-3.5' />
					Open in Google Maps
				</a>
			</Card>
		</button>
	);
};

export const RouteOptimizer: React.FC = () => {
	const { data, loading, error } = useRoutingData();
	const [focusedVehicle, setFocusedVehicle] = useState<number | null>(null);

	const totals = useMemo(() => {
		if (!data) return { routes: 0, distanceKm: 0 };
		const distanceKm = data.routes.reduce((sum, r) => sum + r.distance_km, 0);
		return { routes: data.routes.length, distanceKm: Math.round(distanceKm * 10) / 10 };
	}, [data]);

	if (loading) {
		return (
			<div className='flex h-64 items-center justify-center text-gray-500'>Loading routes…</div>
		);
	}

	if (error || !data) {
		return (
			<Card className='p-6'>
				<div className='text-center text-red-600'>
					{error?.message || 'No routing data available.'}
				</div>
			</Card>
		);
	}

	const { justification } = data;

	// Justification banner tone driven by the verdict.
	const bannerTone =
		justification.verdict === 'under_fleeted'
			? 'border-warning-200 bg-warning-50 text-warning-800'
			: justification.verdict === 'over_fleeted'
				? 'border-success-200 bg-success-50 text-success-800'
				: 'border-neutral-200 bg-neutral-50 text-neutral-800';

	const bannerHeadline =
		justification.verdict === 'under_fleeted'
			? `⚠ Need ${justification.min_autos} autos, you have ${justification.current_fleet} — under-fleeted by ${justification.gap}`
			: justification.verdict === 'over_fleeted'
				? `✓ ${justification.min_autos} autos needed, you run ${justification.current_fleet} — over-fleeted by ${justification.gap}`
				: `✓ Right-sized — ${justification.current_fleet} autos match the ${justification.min_autos} required`;

	return (
		<ErrorBoundary>
			<div className='space-y-6'>
				<PageHeader
					title='Route Optimizer'
					locationName={`${data.city} • ${data.generated}`}
					totalItems={totals.routes}
					itemType='routes'
					icon='🗺️'
				/>

				{/* Fleet-justification banner */}
				<Card className={`border ${bannerTone}`}>
					<div className='font-semibold'>{bannerHeadline}</div>
					<p className='mt-1 text-sm opacity-90'>{justification.justification}</p>
				</Card>

				{/* Summary stats */}
				<div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4'>
					<StatCard icon='🛣️' label='Total Routes' value={totals.routes} color='blue' />
					<StatCard icon='📏' label='Total Distance (km)' value={totals.distanceKm} color='purple' />
					<StatCard icon='🚐' label='Fleet Current' value={data.fleet_current} color='orange' />
					<StatCard icon='🎯' label='Optimal Fleet' value={justification.min_autos} color='green' />
				</div>

				{/* Map (left) + vehicle list (right) */}
				<div className='grid grid-cols-1 gap-4 lg:grid-cols-3'>
					<div className='lg:col-span-2'>
						<Card className='h-[600px] overflow-hidden p-0'>
							{GMAP_API_KEY ? (
								<APIProvider apiKey={GMAP_API_KEY} libraries={['geometry']}>
									<RouteMap
										depot={data.depot}
										routes={data.routes}
										colors={ROUTE_COLORS}
										focusedVehicle={focusedVehicle}
									/>
								</APIProvider>
							) : (
								<div className='flex h-full flex-col items-center justify-center gap-2 bg-background-secondary text-center'>
									<MapPin className='h-10 w-10 text-gray-400' />
									<p className='text-sm font-medium text-gray-600'>
										Set <code className='rounded bg-gray-100 px-1'>VITE_GMAP_API_KEY</code> to view the
										map
									</p>
									<p className='max-w-xs text-xs text-gray-500'>
										Route geometry, depot, and stops will render here once a Google Maps API key is
										configured.
									</p>
								</div>
							)}
						</Card>
					</div>

					<div className='flex max-h-[600px] flex-col gap-3 overflow-y-auto pr-1 lg:col-span-1'>
						{data.routes.map((route, index) => (
							<VehicleCard
								key={route.vehicle}
								route={route}
								color={ROUTE_COLORS[index % ROUTE_COLORS.length]}
								active={focusedVehicle === route.vehicle}
								onSelect={() =>
									setFocusedVehicle(prev => (prev === route.vehicle ? null : route.vehicle))
								}
							/>
						))}
					</div>
				</div>
			</div>
		</ErrorBoundary>
	);
};

export default RouteOptimizer;
